import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  RefreshCw, Search, Link2, Link2Off, Upload,
  CheckCircle, AlertTriangle, Plus, Printer, Tag
} from 'lucide-react';
import { SortableHeader, applySorting } from '../components/SortableHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MenuProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(searchParams.get('filter') || 'all');
  const [pushingCost, setPushingCost] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const handleSort = (field, dir) => { setSortField(field); setSortDirection(dir); };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/menu-products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load menu products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/menu-products/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch { /* silent */ }
  };

  const handleSyncFromLoyverse = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/menu-products/sync-from-loyverse`, {}, { withCredentials: true });
      toast.success(`Synced ${response.data.total_synced} products (${response.data.new_products} new, ${response.data.updated_products} updated)`);
      fetchProducts();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from Loyverse');
    } finally {
      setSyncing(false);
    }
  };

  const handlePushCostToLoyverse = async (loyverseId) => {
    setPushingCost(prev => ({ ...prev, [loyverseId]: true }));
    try {
      const response = await axios.post(
        `${API}/menu-products/${loyverseId}/push-cost-to-loyverse`,
        {},
        { withCredentials: true }
      );
      toast.success(`Cost R${response.data.calculated_cost.toFixed(2)} pushed to Loyverse`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push cost to Loyverse');
    } finally {
      setPushingCost(prev => ({ ...prev, [loyverseId]: false }));
    }
  };

  const handlePrintTag = (loyverseId) => {
    navigate(`/price-tags/print?ids=${loyverseId}`);
  };

  const handleBulkPrintTags = () => {
    if (selectedIds.length === 0) return;
    navigate(`/price-tags/print?ids=${selectedIds.join(',')}`);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredProducts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredProducts.map(p => p.loyverse_id));
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' ||
                       (activeTab === 'linked' && product.has_recipe) ||
                       (activeTab === 'unlinked' && !product.has_recipe);
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesTab && matchesCategory;
  });

  const linkedCount = products.filter(p => p.has_recipe).length;
  const unlinkedCount = products.filter(p => !p.has_recipe).length;
  const allSelected = filteredProducts.length > 0 && selectedIds.length === filteredProducts.length;
  const sortedProducts = applySorting(filteredProducts, sortField, sortDirection);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="menu-products-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Menu Products
          </h1>
          <p className="text-muted-foreground mt-1">
            Sync from Loyverse POS and link recipes to calculate costs
          </p>
        </div>
        <Button onClick={handleSyncFromLoyverse} disabled={syncing} data-testid="sync-loyverse-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync from Loyverse'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Products</p>
                <p className="text-2xl font-bold">{products.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Linked (with recipes)</p>
                <p className="text-2xl font-bold text-green-600">{linkedCount}</p>
              </div>
              <Link2 className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unlinked (need recipes)</p>
                <p className="text-2xl font-bold text-orange-500">{unlinkedCount}</p>
              </div>
              <Link2Off className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">No menu products yet</h3>
              <p className="text-muted-foreground mb-4">
                Sync your products from Loyverse POS to get started
              </p>
              <Button onClick={handleSyncFromLoyverse} disabled={syncing}>
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Sync from Loyverse
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Search, Category Filter, Bulk Actions */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-products-input"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-52" data-testid="category-filter-select">
                    <SelectValue placeholder="All Loyverse categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Loyverse categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedIds.length > 0 && (
                  <Button onClick={handleBulkPrintTags} data-testid="bulk-print-tags-btn">
                    <Printer className="w-4 h-4 mr-2" />
                    Print {selectedIds.length} Tag{selectedIds.length !== 1 ? 's' : ''}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">All ({products.length})</TabsTrigger>
              <TabsTrigger value="linked">Linked ({linkedCount})</TabsTrigger>
              <TabsTrigger value="unlinked">Unlinked ({unlinkedCount})</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-6">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead><SortableHeader label="Product" field="name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                        <TableHead><SortableHeader label="Loyverse Category" field="category" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                        <TableHead><SortableHeader label="Loyverse Price" field="loyverse_price" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                        <TableHead><SortableHeader label="Recipe Cost" field="calculated_cost" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                        <TableHead><SortableHeader label="Status" field="has_recipe" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedProducts.map((product) => (
                        <TableRow key={product.loyverse_id} data-testid={`product-row-${product.loyverse_id}`}
                          className={selectedIds.includes(product.loyverse_id) ? 'bg-primary/5' : ''}>
                          <TableCell>
                            <Checkbox
                              checked={selectedIds.includes(product.loyverse_id)}
                              onCheckedChange={() => toggleSelect(product.loyverse_id)}
                              data-testid={`select-${product.loyverse_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {product.image_url ? (
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-10 h-10 rounded-lg object-cover"
                                />
                              ) : null}
                              <div>
                                <p className="font-medium">{product.name}</p>
                                {product.sku && (
                                  <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">{product.category || '—'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">R{product.loyverse_price?.toFixed(2)}</span>
                          </TableCell>
                          <TableCell>
                            {product.has_recipe ? (
                              <span className="font-medium text-green-600">
                                R{product.calculated_cost?.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {product.has_recipe ? (
                              <Badge variant="default" className="bg-green-600">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Linked
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-orange-500 border-orange-500">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Unlinked
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePrintTag(product.loyverse_id)}
                                title="Print price tag"
                                data-testid={`print-tag-btn-${product.loyverse_id}`}
                              >
                                <Tag className="w-4 h-4" />
                              </Button>
                              {product.has_recipe ? (
                                <>
                                  <Link to={`/recipes/${product.recipe_id}`}>
                                    <Button variant="outline" size="sm">
                                      View Recipe
                                    </Button>
                                  </Link>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handlePushCostToLoyverse(product.loyverse_id)}
                                    disabled={pushingCost[product.loyverse_id]}
                                    data-testid={`push-cost-btn-${product.loyverse_id}`}
                                  >
                                    {pushingCost[product.loyverse_id] ? (
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <Upload className="w-4 h-4 mr-1" />
                                        Push Cost
                                      </>
                                    )}
                                  </Button>
                                </>
                              ) : (
                                <Link to={`/recipes/new?loyverse_id=${product.loyverse_id}&name=${encodeURIComponent(product.name)}`}>
                                  <Button size="sm" data-testid={`create-recipe-btn-${product.loyverse_id}`}>
                                    <Plus className="w-4 h-4 mr-1" />
                                    Create Recipe
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default MenuProducts;
